<template>
  <sp-menu
    class="finder-filter-menu"
    :class="menuModifiers"
    location="bottom-left"
    close-on-content-click="false"
    :label="t('filter_bar_menu_label')"
    :open.prop="open"
    :no-restore-focus="handoffPending"
    @update-open="onUpdateOpen"
    @close="onClosed"
  >
    <sp-button
      slot="activator"
      v-preserve-properties="propertyBindings(triggerAttributes, buttonProps)"
      class="finder-filter-menu__trigger"
      :class="triggerModifiers"
      variant="text"
      densitysmcompact="false"
      color="custom"
      :aria-expanded="open"
      :data-testid="TestId.finder.filterMenuTrigger"
      v-bind="attributeBindings(triggerAttributes, buttonProps)"
    >
      <span class="finder-filter-menu__trigger-content">
        <sp-icon :name="icon" class="finder-filter-menu__trigger-icon"></sp-icon>
        <span class="finder-filter-menu__trigger-label">{{ triggerLabel }}</span>
        <sp-icon name="popup-arrow-filled" size="x-small" class="finder-filter-menu__trigger-arrow"></sp-icon>
      </span>
    </sp-button>

    <div class="finder-filter-menu__panel" :data-testid="TestId.finder.filterMenuPanel">
      <header class="finder-filter-menu__header">
        <span class="finder-filter-menu__title">{{ t("filter_bar_menu_label") }}</span>
        <sp-button
          v-if="resettableCount > 0"
          class="finder-filter-menu__reset"
          size="small"
          variant="text-inline"
          color="danger"
          :title="t('filter_bar_menu_reset_title')"
          :data-testid="TestId.finder.filterMenuReset"
          @click="onClearAll"
        >
          {{ t("filter_bar_menu_reset") }}
        </sp-button>
      </header>

      <ul class="finder-filter-menu__list">
        <li v-for="menuItem in menuItems" :key="menuItem.id" class="finder-filter-menu__row">
          <sp-button
            v-if="canReveal(menuItem)"
            class="finder-filter-menu__row-label"
            :class="menuItemModifiers(menuItem)"
            variant="text"
            densitysmcompact="false"
            color="custom"
            :data-testid="TestId.finder.filterMenuRow"
            @click="onSelect(menuItem)"
          >
            <span class="finder-filter-menu__row-content">
              <sp-icon v-if="menuItem.icon" :name="menuItem.icon" class="finder-filter-menu__row-icon"></sp-icon>
              <span v-else class="finder-filter-menu__row-spacer"></span>
              <span>{{ menuItem.label }}</span>
            </span>
          </sp-button>
          <sp-text v-else class="finder-filter-menu__row-label" :class="menuItemModifiers(menuItem)">
            <span class="finder-filter-menu__row-content">
              <sp-icon v-if="menuItem.icon" :name="menuItem.icon" class="finder-filter-menu__row-icon"></sp-icon>
              <span v-else class="finder-filter-menu__row-spacer"></span>
              <span>{{ menuItem.label }}</span>
            </span>
          </sp-text>
          <sp-button
            v-if="isClearable(menuItem)"
            class="finder-filter-menu__row-clear"
            size="xx-small"
            color="danger"
            icon="close-bold"
            densitysmcompact="false"
            :label="clearLabel(menuItem)"
            :data-testid="TestId.finder.filterMenuRowClear"
            @click="onClear(menuItem)"
          ></sp-button>
        </li>
      </ul>
    </div>

    <div slot="sheet-action" class="finder-filter-menu__actions">
      <sp-button
        class="finder-filter-menu__done"
        color="primary"
        :data-testid="TestId.finder.filterMenuDone"
        @click="onDone"
      >
        {{ t("filter_bar_menu_done") }}
      </sp-button>
    </div>
  </sp-menu>
</template>

<script setup>
/**
 * The bar's `Filters (n)` panel: an index of every filter the bar holds, which of them the visitor
 * has applied, and the controls to clear them. It owns the open state and pending focus handoff;
 * every row and number is derived from the bar's local filters and external contributions.
 */
import { computed, ref, shallowRef } from "vue";

import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { FINDER_FILTER_MENU_ICON } from "./constants";

import { attributeBindings, propertyBindings, vPreserveProperties } from "#ce/utils/property-bindings";
import { props as buttonProps } from "#ce/components/SpButton/props";
import { FINDER_FILTER_COUNT_ATTRIBUTE } from "#ce/components/SpFinder/engine/finder-filter-contract";
import { TestId } from "#/test-ids";
import { t, tAccessibleName } from "#ce/shared/components-i18n";

const props = defineProps({
  icon: {
    type: String,
    default: FINDER_FILTER_MENU_ICON,
  },
  /** @type {import("#ce/components/SpFinderFilterBar/composables/useFinderFilterMenu").FinderMenuItem[]} */
  menuItems: {
    type: Array,
    default: () => [],
  },
  /** How many removable filters and applied external contributions the visitor has. */
  appliedCount: {
    type: Number,
    default: 0,
  },
  /**
   * How many of those Reset All can actually clear. A required external contribution can count as applied but cannot
   * be reset, so the control stays away rather than offering to do nothing.
   */
  resettableCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["select", "before-reveal", "clear", "clear-all"]);

const { isGtSmScreen } = useBreakpoints();

const menuModifiers = computed(() => ({
  "--is-wide": isGtSmScreen.value,
}));

const open = ref(false);
const pendingMenuItem = shallowRef();
const handoffPending = computed(() => Boolean(pendingMenuItem.value));

function canReveal(menuItem) {
  return Boolean(menuItem.actions.open || menuItem.actions.reveal);
}

function onDone() {
  open.value = false;
}

function onClosed() {
  const menuItem = pendingMenuItem.value;
  pendingMenuItem.value = undefined;

  if (menuItem) {
    emit("select", menuItem);
  }
}

const triggerLabel = computed(() =>
  props.appliedCount > 0
    ? t("filter_bar_menu_label_count", {
        count: props.appliedCount,
      })
    : t("filter_bar_menu_label"),
);

const triggerAttributes = computed(() => {
  return {
    [FINDER_FILTER_COUNT_ATTRIBUTE]: props.appliedCount,
  };
});

const triggerModifiers = computed(() => {
  return {
    "--is-applied": props.appliedCount > 0,
  };
});

function menuItemModifiers(menuItem) {
  return {
    "--is-applied": menuItem.state.active,
    "--is-clearable": isClearable(menuItem),
  };
}

function clearLabel(menuItem) {
  return tAccessibleName("filter_bar_menu_clear", {
    label: menuItem.label,
  });
}

/** Only an applied filter the visitor may clear offers the control to do it. */
function isClearable(menuItem) {
  return menuItem.state.active && menuItem.state.clearable;
}

function onUpdateOpen(event) {
  const [value] = event.detail;
  open.value = value;

  if (value) {
    pendingMenuItem.value = undefined;
  }
}

function onSelect(menuItem) {
  if (menuItem.contributionId !== undefined) {
    pendingMenuItem.value = menuItem;
    emit("before-reveal");
    open.value = false;
    return;
  }

  open.value = false;
  emit("select", menuItem);
}

// The menu stays open so a second filter can be cleared without reopening it.
function onClear(menuItem) {
  emit("clear", menuItem);
}

function onClearAll() {
  emit("clear-all");
}
</script>

<style lang="scss" scoped>
$trigger-color: var(
  --sp-comp-finder-filter-bar-menu-color,
  var(--sp-comp-filter-bar-activator-label-color, var(--sp-sys-color-primary))
);
$trigger-height: var(--sp-comp-filter-bar-activator-height);
$trigger-radius: var(--sp-ref-border-radius-lg);
$surface: var(--sp-comp-finder-filter-bar-menu-bg-color, var(--sp-sys-color-surface));

.finder-filter-menu {
  --menu-width: var(--sp-comp-finder-filter-bar-menu-width, 317px);
  --sp-comp-popup-min-width: var(--menu-width);
  --sp-comp-popup-width: var(--menu-width);
  --sp-comp-popup-offset: var(--sp-ref-scale-8);
  --sp-comp-popup-round-coordinates: false;
  --sp-comp-popup-flip: false;
  --sp-comp-popup-box-shadow: var(--sp-comp-finder-filter-bar-menu-shadow, 0 4px 4px 1px rgb(0 0 0 / 25%));
  --sp-comp-menu-sheet-content-padding-block: 0;
  --sp-comp-menu-sheet-action-padding-inline: 15px;
  --sp-comp-bottom-sheet-background-color: #{$surface};
  --sp-comp-dialog-box-shadow: var(--sp-comp-popup-box-shadow);
  --menu-font: var(--sp-comp-finder-filter-bar-menu-font, var(--sp-sys-typescale-body-short));
  --menu-color: var(--sp-comp-finder-filter-bar-menu-row-color, black);
  --menu-row-hover: var(--sp-comp-finder-filter-bar-menu-row-hover, #b2d9dc);

  width: auto;
  font: var(--menu-font);
  color: var(--menu-color);

  &__trigger {
    --sp-comp-button-custom-variant-text-text-color: #{$trigger-color};
    --sp-comp-button-custom-hover-text-color: #{$trigger-color};
    --sp-comp-button-hover-bg-opacity: 0;
    --sp-comp-button-custom-hover-variant-text-bg-opacity: 0;
    --sp-comp-button-focus-opacity: 0;
    --sp-comp-button-focus-visible-opacity: 1;
    --sp-comp-button-focus-visible-outline: auto;
    --sp-comp-button-border-radius: #{$trigger-radius};
    --sp-comp-button-min-height: calc(#{$trigger-height} - 2px);
    --sp-comp-button-size: calc(#{$trigger-height} - 2px);
    --sp-comp-button-padding: 0 25px 0 var(--sp-ref-scale-16);
    --sp-comp-button-width: 100%;
    --sp-comp-button-gap: 0;
    --sp-comp-button-justify-content: start;
    --sp-comp-button-font-short: var(--sp-sys-typescale-body-short);
    --sp-comp-button-text-transform: none;

    min-width: var(--sp-comp-finder-filter-bar-menu-trigger-width, 9rem);
    height: #{$trigger-height};
    box-sizing: border-box;
    border: 1px solid #{$surface};
    border-radius: #{$trigger-radius};
    background: #{$surface};
    box-shadow: var(--sp-comp-finder-filter-bar-menu-trigger-shadow, var(--sp-sys-elevation-1));
  }

  &__trigger-content {
    display: flex;
    align-items: center;
    gap: 3px;
  }

  &:not(.--is-wide) .finder-filter-menu__trigger {
    box-shadow: var(
      --sp-comp-finder-filter-bar-menu-trigger-shadow-mobile,
      var(--sp-comp-finder-filter-bar-menu-trigger-shadow, var(--sp-sys-elevation-1))
    );
  }

  &__trigger-icon {
    --sp-comp-icon-size: var(--sp-ref-scale-20);

    flex: none;
    --sp-comp-icon-fill: var(--sp-comp-finder-filter-bar-menu-icon-fill, var(--sp-sys-color-primary));
  }

  &__trigger-arrow {
    --sp-comp-icon-fill: var(--sp-comp-finder-filter-bar-menu-arrow-fill, #{$trigger-color});

    flex: none;
    transform: translateY(var(--sp-comp-finder-filter-bar-menu-arrow-offset, -1px));
  }

  &__trigger-label {
    font-family: var(--sp-comp-filter-bar-activator-label-font-family, var(--sp-sys-font-family-semi-bold));
    font-weight: var(
      --sp-comp-filter-bar-activator-label-font-weight,
      var(--sp-sys-typescale-body-semi-bold-font-weight)
    );
    font-size: var(
      --sp-comp-filter-bar-activator-label-font-size,
      var(--sp-comp-filter-bar-activator-title-font-size, var(--sp-sys-typescale-body-font-size))
    );
    line-height: 1.1;
    unicode-bidi: plaintext;
    white-space: nowrap;
  }

  &__panel {
    padding-block-start: 17px;
    font: var(--menu-font);
    color: var(--menu-color);
  }

  &__header {
    display: flex;
    align-items: var(--sp-comp-finder-filter-bar-menu-heading-align, end);
    justify-content: space-between;
    padding-inline: var(--sp-ref-scale-16);
    margin-block-end: var(--sp-comp-finder-filter-bar-menu-heading-gap, var(--sp-ref-scale-14));
    font-family: var(--sp-sys-typescale-body-bold-font-family);
    font-weight: var(--sp-sys-typescale-body-bold-font-weight);
    font-size: var(--sp-ref-scale-16);
    line-height: 1;
    color: var(--sp-comp-finder-filter-bar-menu-heading-color, #373e41);
  }

  &__reset {
    display: inline-block;
    line-height: 0;

    --font-family: var(--sp-sys-font-family-medium);
    --font-weight: normal;
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: var(--sp-comp-finder-filter-bar-menu-row-gap, 0.25ch);
    position: relative;
    box-sizing: border-box;
    min-height: var(--sp-comp-finder-filter-bar-menu-row-height, 42px);
    padding: var(--sp-ref-scale-6) var(--sp-ref-scale-16);

    &::after {
      content: "";
      position: absolute;
      inset: auto 0 0;
      height: 1px;
      background: var(--sp-sys-color-outline-variant);
    }

    &:hover {
      background: var(--menu-row-hover);

      &::after {
        inset-block-start: -1px;
        inset-block-end: auto;
        background: var(--menu-row-hover);
      }
    }
  }

  &.--is-wide .finder-filter-menu__row:last-child:not(:hover)::after {
    display: none;
  }

  &__row-label {
    --sp-comp-button-custom-hover-variant-text-bg-opacity: 0;
    --sp-comp-button-custom-variant-text-text-color: var(--menu-color);
    --sp-comp-button-custom-hover-text-color: var(--menu-color);
    --sp-comp-button-font-short: var(--menu-font);
    --sp-comp-button-justify-content: start;
    --sp-comp-button-padding: 0;
    --sp-comp-button-min-width: 0;
    --sp-comp-button-min-height: 0;
    --sp-comp-button-size: auto;
    --sp-comp-button-gap: 0;
    --sp-comp-button-hover-bg-opacity: 0;
    --sp-comp-button-width: 100%;
    --sp-comp-button-content-text-align: start;

    flex: 1;

    &.--is-clearable {
      --sp-comp-button-font-short: var(--sp-comp-finder-filter-bar-menu-active-weight, bold)
        var(--sp-sys-typescale-body-font-size) / var(--sp-sys-typescale-body-line-height)
        var(--sp-sys-typescale-body-font-family);
    }
  }

  &__row-content {
    display: flex;
    align-items: center;
    gap: var(--sp-comp-finder-filter-bar-menu-row-gap, 0.25ch);
  }

  &__row-icon {
    --sp-comp-icon-size: var(--sp-ref-scale-22);
    --sp-comp-icon-fill: var(--sp-sys-color-primary);

    margin-inline: var(--sp-ref-scale-4);
  }

  &__row-spacer {
    width: 30px;
    height: 30px;
  }

  &__actions {
    width: 100%;
    padding-bottom: 15px;
  }

  &__done {
    --sp-comp-button-width: 100%;
  }
}
</style>
